// deviceInfo.ts - Utility to collect device information for login tracking

import { v4 as uuidv4 } from 'uuid';

interface DeviceInfo {
  version_code?: number;
  unique_id: string;
  platform: string;
  device_name: string;
  model: string;
  manufacturer: string;
  ip_address?: string | null;
}

/**
 * Get or create a unique device ID
 * Stored in localStorage to persist across sessions
 */
const getOrCreateDeviceId = (): string => {
  const STORAGE_KEY = 'device_unique_id';
  let deviceId = localStorage.getItem(STORAGE_KEY);
  
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(STORAGE_KEY, deviceId);
  }
  
  return deviceId;
};

/**
 * Detect the operating system and version
 */
const getOS = (): string => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // Windows
  if (userAgent.includes('Windows NT 10.0')) return 'Windows 10';
  if (userAgent.includes('Windows NT 11.0')) return 'Windows 11';
  if (userAgent.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (userAgent.includes('Windows NT 6.2')) return 'Windows 8';
  if (userAgent.includes('Windows NT 6.1')) return 'Windows 7';
  if (userAgent.includes('Windows')) return 'Windows';
  
  // macOS
  if (userAgent.includes('Mac OS X')) {
    const match = userAgent.match(/Mac OS X (\d+[._]\d+[._]\d+)/);
    if (match) {
      const version = match[1].replace(/_/g, '.');
      const majorVersion = parseInt(version.split('.')[0]);
      
      // macOS version names
      if (majorVersion >= 14) return `macOS Sonoma ${version}`;
      if (majorVersion === 13) return `macOS Ventura ${version}`;
      if (majorVersion === 12) return `macOS Monterey ${version}`;
      if (majorVersion === 11) return `macOS Big Sur ${version}`;
      if (majorVersion === 10) {
        const minorVersion = parseInt(version.split('.')[1]);
        if (minorVersion === 15) return `macOS Catalina ${version}`;
        if (minorVersion === 14) return `macOS Mojave ${version}`;
      }
      return `macOS ${version}`;
    }
    return 'macOS';
  }
  
  // Linux
  if (platform.includes('Linux') || userAgent.includes('Linux')) {
    if (userAgent.includes('Ubuntu')) return 'Ubuntu Linux';
    if (userAgent.includes('Fedora')) return 'Fedora Linux';
    if (userAgent.includes('Debian')) return 'Debian Linux';
    return 'Linux';
  }
  
  // Mobile
  if (userAgent.includes('iPhone')) return 'iOS';
  if (userAgent.includes('iPad')) return 'iPadOS';
  if (userAgent.includes('Android')) {
    const match = userAgent.match(/Android (\d+(\.\d+)?)/);
    return match ? `Android ${match[1]}` : 'Android';
  }
  
  return 'Unknown OS';
};

/**
 * Detect browser name and version
 */
const getBrowserInfo = (): { name: string; version: string } => {
  const userAgent = navigator.userAgent;
  
  // Edge (Chromium-based)
  if (userAgent.includes('Edg/')) {
    const match = userAgent.match(/Edg\/(\d+\.\d+\.\d+\.\d+)/);
    return { name: 'Edge', version: match ? match[1] : 'Unknown' };
  }
  
  // Chrome
  if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    const match = userAgent.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/);
    return { name: 'Chrome', version: match ? match[1] : 'Unknown' };
  }
  
  // Firefox
  if (userAgent.includes('Firefox/')) {
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    return { name: 'Firefox', version: match ? match[1] : 'Unknown' };
  }
  
  // Safari
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    return { name: 'Safari', version: match ? match[1] : 'Unknown' };
  }
  
  // Opera
  if (userAgent.includes('OPR/')) {
    const match = userAgent.match(/OPR\/(\d+\.\d+\.\d+\.\d+)/);
    return { name: 'Opera', version: match ? match[1] : 'Unknown' };
  }
  
  return { name: 'Unknown Browser', version: 'Unknown' };
};

/**
 * Get browser vendor/manufacturer
 */
const getBrowserVendor = (): string => {
  const userAgent = navigator.userAgent;
  
  if (userAgent.includes('Edg/')) return 'Microsoft';
  if (userAgent.includes('Chrome/')) return 'Google';
  if (userAgent.includes('Firefox/')) return 'Mozilla';
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Apple';
  if (userAgent.includes('OPR/')) return 'Opera Software';
  
  return 'Unknown';
};

/**
 * Get device type
 */
const getDeviceType = (): string => {
  const userAgent = navigator.userAgent;
  
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
    return 'Tablet';
  }
  
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
    return 'Mobile';
  }
  
  return 'Desktop';
};

/**
 * Generate device description
 */
const getDeviceDescription = (): string => {
  const browser = getBrowserInfo();
  const os = getOS();
  const deviceType = getDeviceType();
  
  return `${browser.name} on ${os} (${deviceType})`;
};

/**
 * Main function to collect all device information
 */
export const collectDeviceInfo = (): DeviceInfo => {
  const browser = getBrowserInfo();
  const os = getOS();
  
  return {
    // version_code: Leave empty as per requirement
    unique_id: getOrCreateDeviceId(),
    platform: os,
    device_name: getDeviceDescription(),
    model: `${browser.name} ${browser.version}`,
    manufacturer: getBrowserVendor(),
    ip_address: null, // Backend will populate this
  };
};

/**
 * Get device info with custom version code (if needed)
 */
export const collectDeviceInfoWithVersion = (versionCode: number): DeviceInfo => {
  return {
    ...collectDeviceInfo(),
    version_code: versionCode,
  };
};

// Export individual functions if needed elsewhere
export {
  getOrCreateDeviceId,
  getOS,
  getBrowserInfo,
  getBrowserVendor,
  getDeviceType,
  getDeviceDescription,
};